/**
 * 参加船名簿フォームの型・初期値・API 変換。入力チェックの本体は API（L-25）。
 * ここでは必須・形式の即時チェックのみを行い、サーバーの指摘は details の path で各項目に戻す。
 */
import { z } from 'zod';
import type { VesselDetail } from './api';

/** カタカナ（全角）・長音・中黒・小書き拡張・半角カナ・空白（API の L-25 と同じ許可範囲） */
const KANA_RANGES: [number, number][] = [
  [0x30a1, 0x30fa],
  [0x30fb, 0x30fc],
  [0x31f0, 0x31ff],
  [0xff65, 0xff9f],
  [0x3000, 0x3000],
  [0x20, 0x20],
];
export const isKana = (v: string): boolean =>
  v.length > 0 && [...v].every((ch) => KANA_RANGES.some(([a, b]) => ch.codePointAt(0)! >= a && ch.codePointAt(0)! <= b));

const person = z.object({
  id: z.number().optional(),
  name: z.string().trim().max(100, '100文字以内で入力してください'),
  approvalFrom: z.string(),
  approvalTo: z.string(),
  note: z.string().max(1000),
});

const capacity = z.object({
  id: z.number().optional(),
  boardingCount: z.number().int('1以上の整数で入力してください').min(1, '乗船人員を入力してください').max(999).nullable(),
  totalBoardingCount: z.number().int('1以上の整数で入力してください').min(1, '全乗船人員を入力してください').max(999).nullable(),
  approvalFrom: z.string(),
  approvalTo: z.string(),
  note: z.string().max(1000),
});

export const vesselFormSchema = z
  .object({
    businessOperatorId: z.number().int().positive('事業実施者を選択してください').nullable(),
    displayOrder: z.number().int().min(0).nullable(),
    ownerName: z.string().trim().min(1, '氏名又は名称を入力してください').max(100, '100文字以内で入力してください'),
    name: z.string().trim().min(1, '船名を入力してください').max(50, '50文字以内で入力してください'),
    nameKana: z
      .string()
      .trim()
      .min(1, '船名（カナ）を入力してください')
      .max(50, '50文字以内で入力してください')
      .refine(isKana, '船名（カナ）は全角カタカナで入力してください'),
    registrationNo: z.string().trim().min(1, '漁船登録番号を入力してください').max(15, '15桁以内で入力してください'),
    tonnage: z.number().min(0, '0以上で入力してください').nullable(),
    horsepower: z.string().trim().max(20),
    address: z.string().trim().max(200),
    fuelType: z.string(),
    taxRounding: z.enum(['四捨五入', '切り捨て', '切り上げ']),
    isInvoiceRegistered: z.boolean(),
    phone: z.string().trim().max(30),
    phoneFrom: z.string(),
    phoneTo: z.string(),
    approvalFrom: z.string(),
    approvalTo: z.string(),
    billingAddressee: z.string(),
    claimantName: z.string().trim().max(100),
    claimantAddress: z.string().trim().max(200),
    claimantInvoiceNo: z.string().trim().max(20),
    charterFeeRecipient: z.string().trim().max(100),
    isMutualAidTarget: z.boolean(),
    note: z.string().max(2000),
    captains: z.array(person).min(1, '船長を1件以上入力してください'),
    crewMembers: z.array(person),
    capacities: z.array(capacity).min(1, '乗船人数を1件以上入力してください'),
    fishingMethods: z.array(z.object({ value: z.string() })).min(1, '漁業種類を1件以上入力してください'),
  })
  .superRefine((v, ctx) => {
    v.captains.forEach((c, i) => {
      if (!c.name.trim()) ctx.addIssue({ code: 'custom', path: ['captains', i, 'name'], message: `${i + 1}行目の船長氏名を入力してください` });
    });
    v.fishingMethods.forEach((m, i) => {
      if (!m.value) ctx.addIssue({ code: 'custom', path: ['fishingMethods', i, 'value'], message: '漁業種類を選択してください' });
    });
  });

export type VesselFormValues = z.infer<typeof vesselFormSchema>;

export const emptyPerson = (): VesselFormValues['captains'][number] => ({
  name: '',
  approvalFrom: '',
  approvalTo: '',
  note: '',
});

export const emptyCapacity = (): VesselFormValues['capacities'][number] => ({
  boardingCount: null,
  totalBoardingCount: null,
  approvalFrom: '',
  approvalTo: '',
  note: '',
});

export function emptyVesselForm(businessOperatorId: number | null): VesselFormValues {
  return {
    businessOperatorId,
    displayOrder: null,
    ownerName: '',
    name: '',
    nameKana: '',
    registrationNo: '',
    tonnage: null,
    horsepower: '',
    address: '',
    fuelType: '',
    taxRounding: '四捨五入',
    isInvoiceRegistered: true,
    phone: '',
    phoneFrom: '',
    phoneTo: '',
    approvalFrom: '',
    approvalTo: '',
    billingAddressee: '',
    claimantName: '',
    claimantAddress: '',
    claimantInvoiceNo: '',
    charterFeeRecipient: '',
    isMutualAidTarget: false,
    note: '',
    captains: [emptyPerson()],
    crewMembers: [],
    capacities: [emptyCapacity()],
    fishingMethods: [{ value: '' }],
  };
}

const s = (v: string | null | undefined) => v ?? '';

export function toForm(d: VesselDetail): VesselFormValues {
  const person = (p: VesselDetail['captains'][number]) => ({
    id: p.id,
    name: p.name,
    approvalFrom: s(p.approvalFrom),
    approvalTo: s(p.approvalTo),
    note: s(p.note),
  });
  return {
    businessOperatorId: d.businessOperatorId,
    displayOrder: d.displayOrder,
    ownerName: d.ownerName,
    name: d.name,
    nameKana: d.nameKana,
    registrationNo: d.registrationNo,
    tonnage: d.tonnage,
    horsepower: s(d.horsepower),
    address: s(d.address),
    fuelType: s(d.fuelType),
    taxRounding: d.taxRounding,
    isInvoiceRegistered: d.isInvoiceRegistered,
    phone: s(d.phone),
    phoneFrom: s(d.phoneFrom),
    phoneTo: s(d.phoneTo),
    approvalFrom: s(d.approvalFrom),
    approvalTo: s(d.approvalTo),
    billingAddressee: s(d.billingAddressee),
    claimantName: s(d.claimantName),
    claimantAddress: s(d.claimantAddress),
    claimantInvoiceNo: s(d.claimantInvoiceNo),
    charterFeeRecipient: s(d.charterFeeRecipient),
    isMutualAidTarget: d.isMutualAidTarget,
    note: s(d.note),
    captains: d.captains.map(person),
    crewMembers: d.crewMembers.map(person),
    capacities: d.capacities.map((c) => ({
      id: c.id,
      boardingCount: c.boardingCount,
      totalBoardingCount: c.totalBoardingCount,
      approvalFrom: s(c.approvalFrom),
      approvalTo: s(c.approvalTo),
      note: s(c.note),
    })),
    fishingMethods: d.fishingMethods.map((value) => ({ value })),
  };
}

/** フォーム値 → API の body（空文字は API 側で null に正規化される） */
export function toBody(v: VesselFormValues, fiscalYear: number, isAdmin: boolean) {
  const { fishingMethods, displayOrder, businessOperatorId, ...rest } = v;
  return {
    ...rest,
    fiscalYear,
    businessOperatorId: businessOperatorId ?? undefined,
    displayOrder: isAdmin && displayOrder != null ? displayOrder : undefined,
    isMutualAidTarget: isAdmin ? rest.isMutualAidTarget : undefined,
    fishingMethods: fishingMethods.map((m) => m.value),
  };
}
